import {
  Component,
  OnInit,
  Injector,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
  ValidatorFn,
  AbstractControl,
} from '@angular/forms'

import { MatButtonModule } from '@angular/material/button'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { Dialog } from '@angular/cdk/dialog'
import { ActivatedRoute, Router } from '@angular/router'
import { AuthService } from '../../../services/auth.service'
import { ApiError } from '../../../types/apiError'
import { ErrorService } from '../../../services/error.service'
import { SpinnerService } from '../../../services/spinner.service'
import { DialogService } from '../../../services/dialog.service'
import { firstValueFrom } from 'rxjs/internal/firstValueFrom'
import { SetPasswordBody } from '../../../types/auth'
import { TranslationService } from '../../../services/translation.service'

@Component({
  selector: 'app-set-password',
  imports: [
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatButtonModule,
    MatIcon,
  ],
  templateUrl: './set-password.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './set-password.component.scss',
})
export class SetPasswordComponent implements OnInit {
  private injector = inject(Injector)
  private formBuilder = inject(FormBuilder)
  dialog = inject(Dialog)
  private route = inject(ActivatedRoute)
  private authService = inject(AuthService)
  private errorService = inject(ErrorService)
  private spinnerService = inject(SpinnerService)
  private dialogService = inject(DialogService)
  private router = inject(Router)
  translationService = inject(TranslationService)

  passwordToken: string = ''
  oneLowerCaseRegex: RegExp = /(?=.*[a-z])/
  oneUpperCaseRegex: RegExp = /(?=.*[A-Z])/
  oneDigitRegex: RegExp = /(?=.*\d)/
  oneSymbolRegex: RegExp = /(?=.*\W)/

  passwordValidation = [
    Validators.required,
    Validators.minLength(10),
    this.customPatternValidation({
      pattern: this.oneLowerCaseRegex,
      msg: this.translationService.getLowerCaseRequiredText(),
    }),
    this.customPatternValidation({
      pattern: this.oneUpperCaseRegex,
      msg: this.translationService.getUpperCaseRequiredText(),
    }),
    this.customPatternValidation({
      pattern: this.oneDigitRegex,
      msg: this.translationService.getNumberRequiredText(),
    }),
    this.customPatternValidation({
      pattern: this.oneSymbolRegex,
      msg: this.translationService.getSymbolRequiredText(),
    }),
  ]

  /**
   * Validates whether the password and confirmPassword fields in the given AbstractControl are equal.
   *
   * @param {AbstractControl} c - The control containing the password and confirmPassword fields.
   * @returns {{ notSame: boolean } | object} - Returns an object with `notSame: true` if the passwords do not match, otherwise returns an empty object.
   */
  validatePasswordsAreEqual(c: AbstractControl): { notSame: boolean } | object {
    return c.value.password === c.value.confirmPassword ? {} : { notSame: true }
  }

  /**
   * Returns a ValidatorFn that validates whether the control's value matches a given pattern.
   *
   * @param {Object} config - The configuration object for the validation.
   * @param {RegExp} config.pattern - The regular expression pattern to test against the control's value.
   * @param {string} config.msg - The error message to return if the validation fails.
   * @returns {ValidatorFn} - A ValidatorFn that validates the control's value against the provided pattern.
   */
  customPatternValidation(config: {
    pattern: RegExp
    msg: string
  }): ValidatorFn {
    return (control: AbstractControl) => {
      const urlRegEx: RegExp = config.pattern
      if (control.value && !control.value.match(urlRegEx)) {
        return {
          invalidMsg: config.msg,
        }
      } else {
        return null
      }
    }
  }

  async ngOnInit() {
    try {
      this.spinnerService.startSpinner()
      this.passwordToken = this.route.snapshot.params['passwordToken']
      await firstValueFrom(
        this.authService.validatePasswordToken(this.passwordToken),
      )
      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(
        this.dialogService,
        apiErr,
        this.dialog,
        undefined,
        this.router,
        '',
      )
    }
  }

  passwordForm = this.formBuilder.group(
    {
      password: ['', [...this.passwordValidation]],
      confirmPassword: ['', [...this.passwordValidation]],
    },
    { validators: [this.validatePasswordsAreEqual] },
  )

  /**
   * Submits form data to api
   */
  async onSubmit() {
    try {
      if (!this.passwordForm.valid) {
        return
      }
      this.spinnerService.startSpinner()
      const passwords = this.passwordForm.value

      if (passwords.password !== passwords.confirmPassword) {
        this.dialogService.showDialog(
          this.dialog,
          this.translationService.getErrorText(),
          this.translationService.getPasswordsDoNotMatchText(),
        )

        this.spinnerService.stopSpinner()
        return
      }

      const passwordBody: SetPasswordBody = {
        passwordToken: this.passwordToken,
        password: passwords.password!,
      }

      await firstValueFrom(this.authService.setPassword(passwordBody))
      this.spinnerService.stopSpinner()
      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getUsersText(),
        this.translationService.getPasswordSetSuccessfullyText(),
        undefined,
        this.router,
        '/auth/login',
      )
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(
        this.dialogService,
        apiErr,
        this.dialog,
        undefined,
        this.router,
        '',
      )
    }
  }
}
